import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Phone, Mail, MessageSquare, Calendar, Clock, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get tomorrow's date as minimum
const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);

const DashboardHilfe = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callbackDate, setCallbackDate] = useState('');
  const [callbackTime, setCallbackTime] = useState('');
  const [callbackPhone, setCallbackPhone] = useState('');
  const [callbackNote, setCallbackNote] = useState('');
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [replyMessages, setReplyMessages] = useState({});

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Response format is { tickets: [...] }
      const ticketsData = response.data.tickets || response.data || [];
      
      // Sort by created_at descending and take last 3
      const sortedTickets = ticketsData
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3);
      
      setTickets(sortedTickets);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      // Don't show error toast if user just doesn't have tickets
      if (error.response && error.response.status !== 404) {
        toast.error('Fehler beim Laden der Support-Tickets');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCallback = async () => {
    if (!callbackDate || !callbackTime || !callbackPhone) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Validate phone number (simple validation)
    const phoneRegex = /^[\d\s\+\-\(\)]+$/;
    if (!phoneRegex.test(callbackPhone)) {
      toast.error('Bitte geben Sie eine gültige Telefonnummer ein');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/support/callbacks`,
        {
          preferred_date: callbackDate,
          preferred_time: callbackTime,
          phone_number: callbackPhone,
          note: callbackNote || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Rückruf erfolgreich angefordert!', {
        description: 'Wir werden Sie zum gewünschten Zeitpunkt kontaktieren.',
      });
      setCallbackDate('');
      setCallbackTime('');
      setCallbackPhone('');
      setCallbackNote('');
    } catch (error) {
      console.error('Error requesting callback:', error);
      toast.error('Fehler beim Anfordern des Rückrufs');
    }
  };

  const handleCreateTicket = async () => {
    if (!ticketSubject || ticketMessage.length < 50) {
      toast.error('Bitte füllen Sie alle Felder aus (min. 50 Zeichen)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/support/tickets`,
        {
          subject: ticketSubject,
          message: ticketMessage,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Ticket erfolgreich erstellt!', {
        description: 'Wir werden uns schnellstmöglich bei Ihnen melden.',
      });
      setTicketSubject('');
      setTicketMessage('');
      fetchTickets(); // Refresh tickets
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Fehler beim Erstellen des Tickets');
    }
  };

  const handleReplyToTicket = async (ticketId) => {
    const replyText = replyMessages[ticketId];
    
    if (!replyText || replyText.length < 10) {
      toast.error('Bitte geben Sie eine Antwort ein (min. 10 Zeichen)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/support/tickets/${ticketId}/messages`,
        {
          message: replyText,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Antwort erfolgreich gesendet!');
      setReplyMessages({ ...replyMessages, [ticketId]: '' });
      fetchTickets(); // Refresh tickets to show new message
    } catch (error) {
      console.error('Error replying to ticket:', error);
      if (error.response?.status === 400) {
        toast.error('Ticket ist bereits gelöst');
      } else {
        toast.error('Fehler beim Senden der Antwort');
      }
    }
  };

  const canReplyToTicket = (ticket) => {
    // User can reply if:
    // 1. Ticket is not resolved
    // 2. Support team has already replied
    if (ticket.status === 'resolved') return false;
    
    const hasAdminReply = ticket.messages?.some(msg => msg.is_admin);
    return hasAdminReply;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-blue-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-amber-600" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'resolved':
        return 'Gelöst';
      case 'in_progress':
        return 'In Bearbeitung';
      default:
        return 'Offen';
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'resolved':
        return 'success';
      case 'in_progress':
        return 'active';
      default:
        return 'warning';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Hilfe & Support</h1>
        <p className="text-gray-600">
          Erstellen Sie ein Support-Ticket oder fordern Sie einen Rückruf an
        </p>
      </div>

      {/* Main Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Ticket erstellen Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#0040FF] flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <CardTitle>Support-Ticket erstellen</CardTitle>
            </div>
            <CardDescription>
              Beschreiben Sie Ihr Anliegen und wir helfen Ihnen weiter
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Betreff
              </label>
              <input
                type="text"
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                placeholder="z.B. Frage zu meiner Kampagne"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0040FF]"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Nachricht (min. 50 Zeichen)
              </label>
              <textarea
                value={ticketMessage}
                onChange={(e) => setTicketMessage(e.target.value)}
                placeholder="Beschreiben Sie Ihr Anliegen..."
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0040FF] resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                {ticketMessage.length} / 50 Zeichen
              </p>
            </div>

            <Button
              className="w-full bg-[#0040FF] hover:bg-[#0030CC]"
              onClick={handleCreateTicket}
              disabled={!ticketSubject || ticketMessage.length < 50}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Ticket erstellen
            </Button>
          </CardContent>
        </Card>

        {/* Rückruf anfordern Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <CardTitle>Rückruf anfordern</CardTitle>
            </div>
            <CardDescription>
              Wählen Sie einen passenden Termin und wir rufen Sie zurück
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Datum
              </label>
              <input
                type="date"
                value={callbackDate}
                onChange={(e) => setCallbackDate(e.target.value)}
                min={tomorrow.toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0040FF]"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Uhrzeit
              </label>
              <select
                value={callbackTime}
                onChange={(e) => setCallbackTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0040FF]"
              >
                <option value="">Bitte wählen...</option>
                <option value="morning">Vormittag (9:00 - 12:00)</option>
                <option value="afternoon">Nachmittag (12:00 - 15:00)</option>
                <option value="evening">Nachmittag (15:00 - 18:00)</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Telefonnummer *
              </label>
              <input
                type="tel"
                value={callbackPhone}
                onChange={(e) => setCallbackPhone(e.target.value)}
                placeholder="z.B. +43 123 456789"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0040FF]"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Notiz (optional)
              </label>
              <textarea
                value={callbackNote}
                onChange={(e) => setCallbackNote(e.target.value)}
                placeholder="z.B. Bevorzugte Sprache, spezifisches Thema..."
                rows={2}
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0040FF] resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                {callbackNote.length} / 200 Zeichen
              </p>
            </div>

            <Button
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              onClick={handleRequestCallback}
              disabled={!callbackDate || !callbackTime || !callbackPhone}
            >
              <Phone className="w-4 h-4 mr-2" />
              Rückruf anfordern
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Tickets */}
      {tickets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Letzte Support-Tickets</CardTitle>
            <CardDescription>Ihre neuesten Anfragen im Überblick</CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {tickets.map((ticket, index) => (
                <AccordionItem key={ticket.id} value={`item-${index}`}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(ticket.status)}
                        <span className="font-medium text-left">{ticket.subject}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={getStatusVariant(ticket.status)}>
                          {getStatusText(ticket.status)}
                        </Badge>
                        <span className="text-sm text-gray-500">
                          {new Date(ticket.created_at).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-1">Ihre Nachricht:</p>
                        <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                          {ticket.message}
                        </p>
                      </div>
                      
                      {ticket.messages && ticket.messages.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-600 mb-2">Antworten:</p>
                          <div className="space-y-2">
                            {ticket.messages.map((msg, idx) => (
                              <div
                                key={idx}
                                className={`p-3 rounded-lg text-sm ${
                                  msg.is_admin
                                    ? 'bg-blue-50 border border-blue-200'
                                    : 'bg-gray-50'
                                }`}
                              >
                                <div className="flex justify-between items-start mb-1">
                                  <span className="font-medium text-xs text-gray-600">
                                    {msg.is_admin ? 'Support-Team' : 'Sie'}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {new Date(msg.created_at).toLocaleDateString('de-DE')}
                                  </span>
                                </div>
                                <p className="text-gray-700">{msg.message}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Reply Section - Only if user can reply */}
                      {canReplyToTicket(ticket) && (
                        <div className="pt-4 border-t border-gray-200">
                          <p className="text-sm font-medium text-gray-700 mb-2">Antworten:</p>
                          <textarea
                            value={replyMessages[ticket.id] || ''}
                            onChange={(e) => setReplyMessages({ ...replyMessages, [ticket.id]: e.target.value })}
                            placeholder="Ihre Antwort hier eingeben..."
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0040FF] resize-none text-sm"
                          />
                          <div className="flex justify-between items-center mt-2">
                            <p className="text-xs text-gray-500">
                              {(replyMessages[ticket.id] || '').length} / 10 Zeichen
                            </p>
                            <Button
                              size="sm"
                              className="bg-[#0040FF] hover:bg-[#0030CC]"
                              onClick={() => handleReplyToTicket(ticket.id)}
                              disabled={!replyMessages[ticket.id] || replyMessages[ticket.id].length < 10}
                            >
                              Antwort senden
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Info wenn Ticket gelöst ist */}
                      {ticket.status === 'resolved' && (
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <span>Dieses Ticket ist gelöst und kann nicht mehr beantwortet werden.</span>
                          </div>
                        </div>
                      )}

                      {/* Info wenn noch keine Admin-Antwort */}
                      {ticket.status !== 'resolved' && !canReplyToTicket(ticket) && (
                        <div className="pt-4 border-t border-gray-200">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Clock className="w-4 h-4 text-amber-600" />
                            <span>Warten auf Antwort vom Support-Team. Sie können antworten, sobald wir geantwortet haben.</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Alternative Contact Methods - Always at bottom */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Email Support */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-[#0040FF]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">E-Mail Support</h3>
                <p className="text-sm text-gray-600 mb-2">Antwort innerhalb von 24 Stunden</p>
                <a
                  href="mailto:support@adsimplify.at"
                  className="text-[#0040FF] hover:underline font-medium text-sm"
                >
                  support@adsimplify.at
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Support */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                <svg 
                  className="w-6 h-6" 
                  fill="#25D366" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">WhatsApp</h3>
                <p className="text-sm text-gray-600 mb-2">Schnelle Antworten per Chat</p>
                <a
                  href="https://wa.me/43123456789"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:underline font-medium text-sm"
                >
                  Chat öffnen →
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardHilfe;
